"use client";
import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useAppSelector } from "lib/redux/hooks";
import { selectIsPremiumUser } from "lib/redux/uiSlice";
import { fetchCountryCode } from "lib/utils/getCountryCode";

interface SidebarSectionCardProps {
  title: string;
  icon: string;
  score: number;
  maxScore: number;
  detailsLink: string;
  isEssential?: boolean;
  requiresPremium?: boolean;
}

export const SidebarSectionCard: React.FC<SidebarSectionCardProps> = ({
  title,
  icon,
  score,
  maxScore,
  detailsLink,
  isEssential = true,
  requiresPremium = false,
}) => {
  const pathname = usePathname();
  const hasPremium = useAppSelector(selectIsPremiumUser);
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [countryCode, setCountryCode] = useState("IN");

  React.useEffect(() => {
    fetchCountryCode(setCountryCode);
  }, []);

  const price = countryCode === "IN" ? "₹69" : "$9";
  
  // Check if current card is active based on pathname
  const isActive = pathname === detailsLink;

  // Calculate score ratio for color coding
  const scoreRatio = score / maxScore;
  const scoreColor = 
    scoreRatio > 0.75 ? 'text-green-600' : 
    scoreRatio >= 0.4 ? 'text-orange-600' : 
    'text-red-600';

  const handleCardClick = (e: React.MouseEvent) => {
    if (requiresPremium && !hasPremium) {
      e.preventDefault();
      setShowPremiumOverlay(true);
    }
  };

  return (
    <div className="relative h-full">
      <Link href={requiresPremium && !hasPremium ? "#" : detailsLink} onClick={handleCardClick}>
        <div
          className={`
            group
            relative
            flex
            flex-row
            items-center
            gap-1
            md:gap-1
            lg:gap-1
            xl:gap-2
            2xl:gap-2
            rounded-[10px]
            md:rounded-[12px]
            lg:rounded-[14px]
            xl:rounded-[16px]
            2xl:rounded-[20px]
            bg-white/[0.32]
            p-1
            md:p-2.5
            lg:p-1
            xl:p-3
            2xl:p-3
            transition-all
            duration-500
            ease-out
            hover:shadow-lg
            cursor-pointer
            backdrop-blur-[71.75px]
            min-h-[65px]
            md:min-h-[70px]
            lg:min-h-[70px]
            xl:min-h-[70px]
            2xl:min-h-[70px]
            ${isActive ? 'bg-gradient-to-br from-[#6369a2] to-[#6369a2] shadow-lg' : 'hover:bg-gradient-to-br hover:from-[#6369a2] hover:to-[#6369a2]'}
          `}
          style={{
            border: '0.77px solid transparent',
            backgroundClip: 'padding-box',
            position: 'relative',
          }}
        >
          {/* Gradient Border */}
          <div
            className="absolute inset-0 rounded-[10px] md:rounded-[12px] lg:rounded-[14px] xl:rounded-[16px] 2xl:rounded-[20px] pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, #4200C6 0%, #F5F5F5 50%, #4200C6 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              padding: '0.77px',
            }}
          />

          {/* Icon on Left */}
          {/* <div className="relative z-10 flex h-3 w-3 md:h-4 md:w-4 lg:h-3 lg:w-3 xl:h-4 xl:w-4 2xl:h-4 2xl:w-4 items-center justify-center flex-shrink-0">
            <Image
              src={`/icons/${icon}Icon.svg`}
              alt={`${title} icon`}
              width={16}
              height={16}
              className={`w-full h-full ${isActive ? 'hidden' : 'group-hover:hidden'}`}
            />
            <Image
              src={`/icons/${icon}IconDark.svg`}
              alt={`${title} icon dark`}
              width={16}
              height={16}
              className={`w-full h-full ${isActive ? 'block' : 'hidden group-hover:block'}`}
            />
          </div> */}

          {/* Right side content */}
          <div className="relative z-10 flex flex-col items-center justify-center flex-1 gap-0.5 md:gap-0.5 min-w-0 overflow-hidden">
            {/* Section Title */}
            <div className={`text-left text-[10px] flex flex-row gap-1 justify-center items-center md:text-[11px] lg:text-xs xl:text-xs 2xl:text-sm font-semibold transition-colors leading-tight truncate ${
              isActive ? 'text-white' : 'text-gray-800 group-hover:text-white'
            }`}>
              <span className="relative z-10 flex h-3 w-3 md:h-4 md:w-4 lg:h-3 lg:w-3 xl:h-4 xl:w-4 2xl:h-4 2xl:w-4 items-center justify-center flex-shrink-0">
            <Image
              src={`/icons/${icon}Icon.svg`}
              alt={`${title} icon`}
              width={16}
              height={16}
              className={`w-full h-full ${isActive ? 'hidden' : 'group-hover:hidden'}`}
            />
            <Image
              src={`/icons/${icon}IconDark.svg`}
              alt={`${title} icon dark`}
              width={16}
              height={16}
              className={`w-full h-full ${isActive ? 'block' : 'hidden group-hover:block'}`}
            />
          </span><span>{title}</span>
            </div>

            {/* Essential Section Label */}
            {/* {isEssential && (
              <p className={`text-left text-[6px] md:text-[7px] lg:text-[7px] xl:text-[8px] 2xl:text-[9px] transition-colors truncate ${
                isActive ? 'text-white/70' : 'text-gray-400 group-hover:text-white/70'
              }`}>
                Essential Section
              </p>
            )} */}

            {/* Score Badge */}
            <div
              className={`
                flex
                items-center
                justify-center
                border border-[#BE79FF]
                rounded-[6px]
                md:rounded-[7px]
                lg:rounded-[8px]
                xl:rounded-[9px]
                2xl:rounded-[10px]
                px-1
                md:px-1
                lg:px-1.5
                xl:px-1.5
                2xl:px-2
                py-0.5
                md:py-0.5
                lg:py-1
                xl:py-1
                2xl:py-1
                shadow-sm
                transition-all
                
                ${isActive ? 'bg-white' : 'bg-[#7EB3F8] group-hover:bg-white'}
              `}
            >
              <span className={`text-[11px] md:text-xs lg:text-[11px] xl:text-[13px] 2xl:text-[14px] font-bold ${scoreColor}`}>
                {Math.round(score)}
              </span>
              <span className={`mx-0.5 text-[11px] md:text-xs lg:text-[11px] xl:text-[13px] 2xl:text-[14px] font-medium ${
                isActive ? 'text-[#458ef7]' : 'text-white group-hover:text-[#458ef7]'
              }`}>/</span>
              <span className={`text-[11px] md:text-xs lg:text-[11px] xl:text-[13px] 2xl:text-[14px] font-medium ${
                isActive ? 'text-[#458ef7]' : 'text-white group-hover:text-[#458ef7]'
              }`}>
                {maxScore}
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* Premium Overlay */}
      {requiresPremium && !hasPremium && (
        <div className={`absolute h-full inset-0 transition-opacity duration-300 ${showPremiumOverlay ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}>
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Background card */}
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="absolute inset-0 rounded-[12px] lg:rounded-[16px] xl:rounded-[18px] 2xl:rounded-[20px] bg-gradient-to-br from-[#151515] via-[#272626] to-[#2D2D2D]" />

              {/* Arrow background image */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Image src="/icons/score-check/Arrowbg.svg" alt="arrow-bg" width={400} height={400} className="opacity-20" />
              </div>

              {/* Card content */}
              <div className="relative z-10 w-full h-full flex flex-col items-center justify-center p-3">
                <div className="max-w-xs text-center">
                  <h3 className="text-white text-[10px] font-bold">Upgrade to premium</h3>
                  <p className="text-[10px] text-gray-300 mt-0.5">to upgrade your <span className="text-blue-400">resume</span> game</p>

                  <div className="mt-1">
                    <p className="text-[22px] font-bold text-[#e9ffb3] leading-none">{price}<span className="text-white text-[8px] ml-3">Only</span></p>
                  </div>

                  <Link href="/premium-subscription" className="inline-block text-xs mt-1.5 bg-gradient-to-br from-[#1F47E2] to-[#8CA3FF] text-white px-4 py-1 rounded-full shadow-lg hover:opacity-95">
                    Continue
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {showPremiumOverlay && (
            <button
              className="absolute top-2 right-2 z-20 bg-white/30 rounded-full p-1 hover:bg-white/50 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPremiumOverlay(false);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarSectionCard;
